import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { ConfigurationsSection } from 'src/components/organisms/Configurations/ConfigurationsSection';
import { useGetOrganisationQuery } from 'src/graphql';
import { useTheme } from 'styled-components';
import { AddToSlackButton } from './AddToSlackButton';
import {
  InstallButtonsContainer,
  SlackButtonContainer,
  SlackInstallContainer,
  StyledLink,
  UninstallSlackLink,
} from './SlackConfigurationSection.styles';
import { slackConfigurationSectionVm } from './SlackConfigurationSection.vm';

const TOASTS = {
  SUCCESS: 'slack-installation-success',
  FAILURE: 'slack-installation-failure',
};

export const SlackConfigurationSection = () => {
  const theme = useTheme();
  const { data } = useGetOrganisationQuery();

  const [params] = useSearchParams();
  const msg = params.get('msg');

  useEffect(() => {
    if (msg === TOASTS.SUCCESS) {
      handleSuccessToast({
        toastMessage: 'Successfully installed Slack',
        toastDescription:
          "Vault's Slack app is now successfully installed to your organisations Slack workspace. Employees will now be able to begin using Slack Intake.",
      });
    } else if (msg === TOASTS.FAILURE) {
      handleErrorToast({
        toastMessage: 'Something went wrong',
        toastDescription:
          "We were unable to install Vault's Slack app to your organisation's Slack workspace. Try again and if the issue persists, please reach out via technical support.",
      });
    }
  }, [msg]);

  const { workspaces, hasIntegration, hasAtLeastOneInstallation } =
    slackConfigurationSectionVm({
      organisation: data?.organisation,
    });

  if (!hasIntegration) {
    return null;
  }

  return (
    <ConfigurationsSection
      subheader="Slack Intake"
      description={
        <>
          <span>{`To install Slack Intake, you must be an admin of your organisation's Slack workspace.`}</span>
          <br />
          <span>
            Click <strong>&quot;Install to Slack&quot;</strong> and follow the
            instructions to connect your Slack workspace to Vault, or copy the
            link and send to your Slack workspace admin to complete
            installation.
          </span>
          <br />
          <StyledLink href="https://help.vaultplatform.com/en/articles/11393719-how-does-vault-integrate-with-slack">
            {'Learn more about Slack Intake'}
          </StyledLink>
        </>
      }
      right={
        <InstallButtonsContainer>
          {workspaces.map(
            ({ hasInstalled, teamId, installedAt, workspaceName }) => (
              <SlackInstallContainer key={teamId} hasInstalled={hasInstalled}>
                <SlackButtonContainer>
                  <AddToSlackButton
                    workspaceName={workspaceName}
                    installed={hasInstalled}
                    teamId={teamId}
                    installedAt={installedAt}
                    onCopy={() =>
                      handleSuccessToast({
                        toastMessage: 'Copied to clipboard',
                      })
                    }
                  />
                </SlackButtonContainer>
              </SlackInstallContainer>
            ),
          )}
          {hasAtLeastOneInstallation && (
            <UninstallSlackLink
              href="https://slack.com/intl/en-gb/help/articles/360003125231-Remove-apps-and-customised-integrations-from-your-workspace"
              target="_blank"
            >
              <StyledIcon
                className={'uil-info-circle'}
                size="20px"
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.neutral.N600
                    : theme.atlas.semantic.color.type.muted
                }
                style={{ marginRight: '5px' }}
              />
              Want to uninstall Slack Intake?
            </UninstallSlackLink>
          )}
        </InstallButtonsContainer>
      }
    />
  );
};
